import AdyenCheckout from '@adyen/adyen-web';
import { useCallback, useEffect } from 'react';
import { normalizeLocaleForAdyen } from '../config/Adyen.static.js';
import { APPLE_PAY_CONTAINER_ID } from '../config/consts.js';
import { extractApplePayToken } from '../features/ApplePay/AdyenApplePayButton/AdyenApplePayButton.static.js';
import { applePayTracking } from '../services/diiService.js';
import { safeStringify } from '../utils/utils.js';
import { useTracking } from './useTracking.js';

/**
 * Effect hook over the initialization of the 'applepay' of AdyenCheckout.
 */
const useAdyenApplePay = (config) => {
    const { adyenEnvironment, clientKey, locale, countryCode, currency, amount, paymentMethodsResponse, onSubmit, onFatalError, buttonColor, buttonType, siteId, sid, customerId, customerType, } = config;
    const track = useTracking({
        sid,
        siteId,
        currency,
        customerId,
        guest: customerType === 'GUEST_CUSTOMER',
    });
    /**
     * Handle when Apple Pay button is clicked. Currently, the main purpose of this function is to add Adobe tracking
     * when the Apple Pay payment sheet is open. Therefore, this function does nothing but add a tracking event, then
     * call "resolve()" immediately to continue with the original operation, and let the payment sheet open.
     *
     * Side note: Use 'onClicked' event to track whether the payment sheet open is not as precise as we want, but this
     * would solve the job for now.
     *
     * @see https://docs.adyen.com/payment-methods/apple-pay/web-component?tab=adyen-certificate-intro_1#ap-events
     */
    const onApplePayButtonClicked = useCallback((resolve) => {
        if (track) {
            track('PaymentSheetOpen');
        }
        else {
            applePayTracking('PaymentSheetOpen');
        }
        // Call "resolve()" to continue with the original click event and open the payment sheet
        resolve();
    }, []);
    /**
     * Handle when the Apple Pay payment is submitted.
     *
     * @param state The state of this Apple Pay payment event, this state contains the information of the payment,
     * as well as the Apple Pay token.
     */
    const onApplePaySubmitted = useCallback((state) => {
        const applePayToken = extractApplePayToken(state);
        if (applePayToken !== null) {
            onSubmit(applePayToken);
            if (track) {
                track('PaymentSheetCloseOK');
            }
            else {
                applePayTracking('PaymentSheetCloseOK');
            }
        }
        else {
            onFatalError(new Error(`Failed to extract the applePayToken from ${safeStringify(state)}`));
        }
    }, [onFatalError, onSubmit]);
    useEffect(() => {
        let applePayComponent = null;
        async function initialize() {
            try {
                const adyenLocale = normalizeLocaleForAdyen(locale);
                const adyen = await AdyenCheckout({
                    environment: adyenEnvironment,
                    clientKey,
                    locale: adyenLocale,
                    paymentMethodsResponse,
                });
                applePayComponent = adyen.create('applepay', {
                    onSubmit: onApplePaySubmitted,
                    countryCode,
                    amount: {
                        value: amount,
                        currency,
                    },
                    buttonColor,
                    buttonType,
                    onError: (error) => {
                        onFatalError(error);
                        if (track) {
                            track('PaymentSheetCloseKO');
                        }
                        else {
                            applePayTracking('PaymentSheetCloseKO');
                        }
                    },
                    onClick: onApplePayButtonClicked,
                });
                await applePayComponent.isAvailable();
                applePayComponent.mount(`#${APPLE_PAY_CONTAINER_ID}`);
            }
            catch (error) {
                onFatalError(error);
            }
        }
        initialize();
        return () => {
            if (applePayComponent) {
                applePayComponent.unmount();
            }
        };
    }, [
        adyenEnvironment,
        locale,
        clientKey,
        countryCode,
        amount,
        currency,
        paymentMethodsResponse,
        onSubmit,
        onFatalError,
        buttonType,
        buttonColor,
    ]);
};

export { useAdyenApplePay as default };
//# sourceMappingURL=useAdyenApplePay.js.map
